import {
  useKineticMap,
  useStableResultData,
  MapContext,
  DEFAULT_ZOOM,
  useMapBoxSearchContext,
  MapSearchResultItem,
  useMapSearchBoxPin,
  MapSearchResultProps,
  useAppSearchHistory,
  publicUrl,
  AppSearchItemType,
  AppSearchIconKey,
  useGetNodes,
} from '@kinetic-ui/shared';
import { useAssetWorkOrdersListQuery } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import WorkOrderIcon from '@mui/icons-material/Construction';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/consts';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();

  const result = useAssetWorkOrdersListQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });
  const resultData = useStableResultData(result);
  const { hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();

  const workOrders = useGetNodes(resultData?.assetWorkOrders);

  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const onClick = useCallback(
    (id: string, coordinates: [number, number], openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const workOrder = workOrders.find((e) => e.id === id);
        if (workOrder?.orderNumber) {
          addSearchHistory({
            name: workOrder.orderNumber,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.AssetManagement,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin({ type: 'Point', coordinates: coordinates ?? [0, 0] });
        return;
      }

      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.setViewState({
        latitude: coordinates[1],
        longitude: coordinates[0],
        zoom: DEFAULT_ZOOM,
      });
    },
    [
      addMapPin,
      addSearchHistory,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      workOrders,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {workOrders.map((workOrder) => {
        return (
          <MapSearchResultItem
            key={`searchItem_${workOrder.id}`}
            name={workOrder.orderNumber}
            onClick={(openInNewWindow) =>
              onClick(workOrder.id, workOrder.location?.geometry?.coordinates, openInNewWindow)
            }
            icon={<WorkOrderIcon />}
            newWindow={isAppSearch}
          />
        );
      })}
    </>
  );
};

export default MapSearchResult;
